import React, { useState, useEffect } from 'react';
import { FiX, FiAlertCircle } from 'react-icons/fi';
import { paymentService } from '../../services';

export interface PaymentIframeModalProps {
  isOpen: boolean;
  paymentUrl: string;
  transactionId: string | null;
  onClose: () => void;
  onPaymentComplete: (status: 'completed' | 'failed') => void;
}

const PaymentIframeModal: React.FC<PaymentIframeModalProps> = ({
  isOpen,
  paymentUrl,
  transactionId,
  onClose,
  onPaymentComplete,
}) => {
  const [polling, setPolling] = useState(false);

  // Poll para verificar se o pagamento foi concluído
  useEffect(() => {
    if (!isOpen || !transactionId) return;

    // Começar a verificar após 10s (tempo para o user pagar)
    const startDelay = setTimeout(() => {
      setPolling(true);
    }, 10000);

    return () => {
      clearTimeout(startDelay);
      setPolling(false);
    };
  }, [isOpen, transactionId]);

  useEffect(() => {
    if (!polling || !transactionId) return;

    const interval = setInterval(async () => {
      try {
        const result = await paymentService.getPaymentStatus(transactionId);
        if (result.success && result.status && result.status !== 'pending') {
          clearInterval(interval);
          onPaymentComplete(result.status as 'completed' | 'failed');
        }
      } catch {
        // Ignorar erros de polling
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [polling, transactionId, onPaymentComplete]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-pagali-gray-200">
          <h3 className="text-lg font-semibold text-pagali-gray-800">Efetue o seu pagamento</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-pagali-gray-100 text-pagali-gray-500 transition-colors"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        <div className="p-3 bg-amber-50 border-b border-amber-200">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <FiAlertCircle className="flex-shrink-0" />
            Não atualize nem feche a página durante o pagamento.
          </p>
        </div>

        <div className="h-[560px]">
          <iframe
            src={paymentUrl}
            name="sispPaymentFrame"
            title="Payment Page"
            className="w-full h-full border-0"
            allow="payment"
          />
        </div>

        <div className="p-4 border-t border-pagali-gray-200">
          <button onClick={onClose} className="w-full btn-secondary text-pagali-error">
            Cancelar Pagamento
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentIframeModal;
